import React, { useState, useCallback, useEffect } from 'react'
import * as R from 'ramda'
import { motion, AnimatePresence } from 'motion/react'
import { usePopper } from 'react-popper'
import { cn } from 'design-system'

import { contains, debouncedFocus } from 'src/lib/util/dom'

import useKeyHandlers from 'src/hooks/useKeyHandlers'
import useIsHovered from 'src/hooks/useIsHovered'
import useDebounce from 'src/hooks/useDebounce'
import useViewport from 'src/hooks/useViewport'
import useResizeObserver from 'src/hooks/useResizeObserver'

import classes from './next-popover.module.scss'

const Popover = ({
  open,
  tooltip = false,
  focusOnMount = false,
  onHover,
  placement = 'top',
  strategy = 'fixed',
  fallbackPlacements = ['top', 'right', 'bottom', 'left'],
  anchorElement = null,
  renderTrigger,
  // Extra props forwarded to the renderTrigger callback (alongside `ref`) so
  // wrappers like PopoverMenu can put ARIA state on the trigger element.
  triggerProps = {},
  popoverRef = null,
  onRequestClose,
  style = '',
  className = '',
  afterClose = () => null,
  children
}) => {
  const [triggerElement, setTriggerElement] = useState(null)
  const [arrowElement, setArrowElement] = useState(null)
  const [popoverElement, setPopoverElement] = useState(null)

  const popoverElementRef = useCallback(
    el => {
      if (el) {
        setPopoverElement(el)

        if (popoverRef) {
          popoverRef.current = el
        }
      }
    },
    [popoverRef]
  )

  const referenceElement = anchorElement || triggerElement

  const isTriggerHovered = useIsHovered(onHover && referenceElement)
  const isPopoverHovered = useIsHovered(onHover && popoverElement)

  const { maxLg } = useViewport()

  const { styles, attributes, update } = usePopper(
    referenceElement,
    popoverElement,
    {
      placement,
      strategy,
      modifiers: [
        { name: 'arrow', options: { element: arrowElement } },
        {
          name: 'flip',
          options: {
            fallbackPlacements
          }
        },
        {
          name: 'preventOverflow',
          options: {
            padding: { top: maxLg ? 72 : 8, right: 8, bottom: 8, left: 8 }
          }
        }
      ]
    }
  )

  const handleEsc = event => {
    if (onRequestClose) {
      onRequestClose(event)
    }
    if (R.is(Node, referenceElement)) {
      referenceElement.focus()
    }
  }

  useKeyHandlers(referenceElement, { ESC: handleEsc }, [
    referenceElement,
    onRequestClose
  ])
  useKeyHandlers(popoverElement, { ESC: handleEsc }, [
    popoverElement,
    onRequestClose
  ])

  // Honor `onHover` prop
  useDebounce(isTriggerHovered || isPopoverHovered, 750, isSystemHovered => {
    const triggerFocused = contains(referenceElement, document.activeElement)
    const popoverFocused = contains(popoverElement, document.activeElement)

    if (onHover && isSystemHovered) {
      onHover()
    } else if (
      open &&
      onRequestClose &&
      !isSystemHovered &&
      !triggerFocused &&
      !popoverFocused
    ) {
      onRequestClose()
    }
  })

  // honor `focusOnMount` prop
  useEffect(() => {
    if (open && focusOnMount && !tooltip) {
      const input = popoverElement && popoverElement.querySelector('input')
      if (input) {
        debouncedFocus(input)
      } else if (popoverElement) {
        debouncedFocus(popoverElement)
      }
    }
  }, [open, focusOnMount, tooltip, popoverElement])

  // asynchronous popper update when things change
  const resizeCallback = useCallback(() => {
    if (open && update) {
      update()
    }
  }, [open, update])

  useResizeObserver({
    element: popoverElement,
    callback: resizeCallback
  })

  // Listen for exterior click/focus to close popover (using blur will mess with ability to toggle in firefox)
  useEffect(() => {
    const handleOuterClickFocus = event => {
      const isTrigger = contains(referenceElement, event.target)
      const isChild = contains(popoverElement, event.target)

      if (onRequestClose && !isTrigger && !isChild) {
        onRequestClose(event)
      }
    }

    if (open) {
      window.addEventListener('focusin', handleOuterClickFocus)
      window.addEventListener('mousedown', handleOuterClickFocus)
    }

    return () => {
      if (open) {
        window.removeEventListener('focusin', handleOuterClickFocus)
        window.removeEventListener('mousedown', handleOuterClickFocus)
      }
    }
  }, [referenceElement, popoverElement, open, onRequestClose])

  return (
    <>
      {renderTrigger &&
        renderTrigger({ ref: setTriggerElement, ...triggerProps })}
      <AnimatePresence onExitComplete={afterClose}>
        {open && (
          <motion.div
            {...attributes.popper}
            tabIndex="-1"
            ref={popoverElementRef}
            style={{ ...styles.popper, ...style }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={cn(classes.popover, {
              [classes['popover--tooltip']]: tooltip
            })}
            {...(tooltip && { 'aria-hidden': true })}>
            <div
              className={cn(
                'max-w-md overflow-hidden rounded-2xl shadow-2',
                classes.popover__inner,
                className
              )}>
              {children}
            </div>
            <div
              ref={setArrowElement}
              role="presentation"
              className={cn(classes.arrow, {
                [classes['arrow--tooltip']]: tooltip
              })}
              style={styles.arrow}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Popover
